import { startTour } from '@/features/guided-tour/store'

import { DEMO_TOUR_ID } from './id'

/**
 * The one door to the demo tour's definition, and the reason it costs a
 * production bundle nothing.
 *
 * `import.meta.env.VITE_INCLUDE_DEMO` is a build-time literal (folded in
 * `vite.config.ts`, same mechanism as `VITE_INCLUDE_MOCKS` in `main.tsx`), so in
 * a non-demo build the guard below is a provably-false branch and Rollup drops
 * the `import()` along with the whole chunk — copy, toast, step script. The
 * runtime `isDemoPresentation` flag can't do that job: it flows through a zod
 * parse, so it isn't statically evaluable.
 *
 * Idempotent — every caller shares one in-flight promise, so the sidebar trigger
 * and the auto-start can both call it without racing to register twice.
 */
let pending: Promise<void> | null = null

const loadDemoTour = (): Promise<void> => {
	if (import.meta.env.VITE_INCLUDE_DEMO !== 'true') return Promise.resolve()

	pending ??= import('./demo-tour').then(({ registerDemoTour }) => {
		registerDemoTour()
	})

	return pending
}

/**
 * Load the demo tour's definition, then run it from S01.
 *
 * Awaiting the chunk before starting is load-bearing: the host resolves the
 * definition the moment `activeTourId` flips, and an unregistered id renders
 * nothing at all.
 */
export const startDemoTour = async () => {
	await loadDemoTour()
	startTour(DEMO_TOUR_ID)
}
